import { useState } from "react";
import {
  PLATFORM_TOUR_SKIPPED,
  PLATFORM_TOUR_WATCH_CLICKED,
  PLATFORM_TOUR_WIDGET_DISMISSED,
} from "constants/analyticsEvents";
import {
  PLATFORM_TOUR_LAYOUT_DURATION_S,
  PLATFORM_TOUR_SOURCE,
  PLATFORM_TOUR_STATUS,
} from "constants/productTour";
import { useAnalytics } from "hooks/useAnalytics";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { usePlatformTour } from "hooks/usePlatformTour";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useGetCurrentTermsOfServiceQuery } from "store/apis/termsOfService";
import {
  closePlatformTourPlayer,
  openPlatformTourPlayer,
} from "store/slices/platformTour";
import { PlatformTourPlayerModal } from "./PlatformTourPlayerModal";
import { PlatformTourWelcomeModal } from "./PlatformTourWelcomeModal";
import { PlatformTourWidget } from "./PlatformTourWidget";

type Morph = { video: HTMLVideoElement | null };

export const PlatformTourGate = () => {
  const dispatch = useAppDispatch();
  const { isCustomer } = useRoleBasedView();
  const { trackEvent } = useAnalytics();
  const { success } = useToast();
  const { shouldShowModal, shouldShowWidget, updateTour } = usePlatformTour();

  const [morph, setMorph] = useState<Morph | null>(null);
  const isMorphing = morph !== null;

  const isPlayerOpen = useAppSelector(
    (state) => state.platformTour.isPlayerOpen
  );

  const { data: termsOfService } = useGetCurrentTermsOfServiceQuery(undefined, {
    skip: !isCustomer,
  });

  const isTermsPending = Boolean(termsOfService && !termsOfService.is_accepted);
  const isModalOpen = shouldShowModal && !isTermsPending && !isPlayerOpen;
  const isWidgetOpen = shouldShowWidget && !isPlayerOpen;

  const startMorph = (video: HTMLVideoElement | null = null) => {
    setMorph({ video });
    window.setTimeout(
      () => setMorph(null),
      PLATFORM_TOUR_LAYOUT_DURATION_S * 1000
    );
  };

  const handleWatch = () => {
    trackEvent(PLATFORM_TOUR_WATCH_CLICKED, {
      source: PLATFORM_TOUR_SOURCE.WELCOME_MODAL,
    });
    void updateTour({ status: PLATFORM_TOUR_STATUS.IN_PROGRESS });
    dispatch(openPlatformTourPlayer(PLATFORM_TOUR_SOURCE.WELCOME_MODAL));
  };

  const handleSkip = () => {
    if (isMorphing) return;

    trackEvent(PLATFORM_TOUR_SKIPPED);
    startMorph();
    void updateTour({ status: PLATFORM_TOUR_STATUS.SKIPPED });
  };

  const handleOpenFromWidget = () => {
    trackEvent(PLATFORM_TOUR_WATCH_CLICKED, {
      source: PLATFORM_TOUR_SOURCE.WIDGET,
    });
    dispatch(openPlatformTourPlayer(PLATFORM_TOUR_SOURCE.WIDGET));
  };

  const handleDismissWidget = () => {
    trackEvent(PLATFORM_TOUR_WIDGET_DISMISSED);
    void updateTour({ is_widget_dismissed: true });
    success({
      title: "Platform tour closed",
      message: "Revisit it anytime from Get help",
    });
  };

  const closePlayer = () => dispatch(closePlatformTourPlayer());

  const handleClosePlayer = (
    isCompleted: boolean,
    video?: HTMLVideoElement | null
  ) => {
    if (isCompleted) {
      void updateTour({ status: PLATFORM_TOUR_STATUS.COMPLETED });
    }

    if (!isCompleted && shouldShowWidget) {
      startMorph(video);
    }

    closePlayer();
  };

  return (
    <>
      <PlatformTourWelcomeModal
        open={isModalOpen}
        onWatch={handleWatch}
        onSkip={handleSkip}
      />
      <PlatformTourWidget
        open={isWidgetOpen}
        isMorphing={isMorphing}
        landingVideo={morph?.video ?? null}
        onOpenPlayer={handleOpenFromWidget}
        onDismiss={handleDismissWidget}
      />
      <PlatformTourPlayerModal
        open={isPlayerOpen}
        onClose={handleClosePlayer}
      />
    </>
  );
};
